import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Button,
    CircularProgress,
    Grid,
    Menu,
    MenuItem,
    Stack,
    Tab,
    Tabs,
    Typography
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import SectionHeader from '../../components/SectionHeader';
import SearchToolbar from '../../components/SearchToolbar/SearchToolbar';
import useVisitasRoam from './hooks/useVisitasRoam';
import GrillaVisitasRoam from './components/GrillaVisitasRoam';
import FiltrosVisitasRoam from './components/FiltrosVisitasRoam';
import { VisitaApi } from '../Visitas/consts/types';
import { VisitaEstado } from '../Visitas/consts/visitasStatus';
import { VISIT_TYPE_DISPLAY_NAME_OVERRIDES } from '../Visitas/consts/visitTypeDisplayNames';
import useVisitsSelection from '../Visitas/hooks/useVisitsSelection';
import { useDescargaPdfInvestigacionVisita } from '../Visitas/hooks/useDescargaPdfInvestigacionVisita';
import { puedeDescargarPdfInvestigacion } from '../Visitas/consts/pdfInvestigacion';
import { useDescargaInformacionAdicional } from '../Visitas/hooks/useDescargaInformacionAdicional';
import { puedeDescargarInformacionAdicional } from '../Visitas/consts/informacionAdicional';
import DrawerManager, {
    DRAWER_ACTION_VALUES,
    DrawerAction
} from '../Visitas/components/drawers/DrawerManager';
import { useAppDispatch } from '../../hooks/reduxHooks';
import { useDownloadVisitaActaMutation } from '../../services/visitasApi';
import { useLazyGetPresentacionRGRLPDFbyIdQuery } from '../../services/presentacionesApi';
import { showAlert } from '../../features/slices/applicationSlice';
import { VISIT_TABS_INDEX } from '../../redux/slices/visitsSlice';
import { useRoles } from '../../hooks/useRoles';
import { getAccessValues } from '../../components/layout/RoutesManager/access';
import useUserAccess from '../../hooks/useUserAccess';

const ESTADOS_ACTIVOS = [VisitaEstado.PENDIENTE, VisitaEstado.PROGRAMADA, VisitaEstado.EN_PROCESO];
const REJECTABLE_VISIT_STATES = [VisitaEstado.PENDIENTE, VisitaEstado.PROGRAMADA];
const ACTA_DOWNLOAD_ALLOWED_STATES = [VisitaEstado.PRESENTADO, VisitaEstado.REALIZADA];

function VisitasRoamIndex() {
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation('visitas');
    const dispatch = useAppDispatch();
    const [downloadVisitaActa] = useDownloadVisitaActaMutation();
    const [getPresentacionRGRLPDFbyId] = useLazyGetPresentacionRGRLPDFbyIdQuery();

    const { isAdminBO, isPreventor } = useRoles();
    const { descargandoPdfInvestigacion, descargarPdfInvestigacion } =
        useDescargaPdfInvestigacionVisita();
    const { descargandoInformacionAdicional, descargarInformacionAdicional } =
        useDescargaInformacionAdicional();
    const access = useUserAccess();
    const FEATURES = getAccessValues(access);

    const navigationState = location.state as
        | { tabIndex?: VISIT_TABS_INDEX; idsEstadoFiltro?: number }
        | null;
    const initialTabIndex = navigationState?.tabIndex;
    const initialEstado = navigationState?.idsEstadoFiltro;

    const {
        visitas,
        totalCount,
        loading,
        error,
        filtros,
        currentTabIndex,
        handleTabClick,
        onFiltroChange,
        onLimpiarFiltros,
        onPaginationChange
    } = useVisitasRoam(initialTabIndex, initialEstado);

    useEffect(() => {
        if (error) enqueueSnackbar(error, { variant: 'error' });
    }, [error]);

    const canSelectMultipleVisits =
        FEATURES.GESTION_DE_VISITAS.hasAccess &&
        isPreventor &&
        currentTabIndex === VISIT_TABS_INDEX.PENDING;

    const {
        isChecked,
        isAllChecked,
        handleAllCheckPress,
        handleCheckPress,
        selectedVisitsIds,
        clearVisitsSelection
    } = useVisitsSelection(visitas);

    const selectedVisitsSummary = useMemo(() => {
        const visibleVisitsById = new Map(visitas.map((visit: VisitaApi) => [visit.id, visit]));

        return selectedVisitsIds.map((id) => {
            const visitData = visibleVisitsById.get(id) as any;
            const establishmentName =
                visitData?.establecimiento?.nombre || visitData?.nombreEstablecimiento || `#${id}`;
            const visitTypes = visitData?.actividades
                ?.map(
                    (activity: any) =>
                        VISIT_TYPE_DISPLAY_NAME_OVERRIDES[activity?.tipoVisita?.id] ||
                        activity?.tipoVisita?.descripcion
                )
                .filter(Boolean)
                .join(', ');

            return {
                id,
                label: visitTypes ? `${establishmentName} - ${visitTypes}` : establishmentName,
                subLabel: visitData?.establecimiento?.codigoSRT
            };
        });
    }, [selectedVisitsIds, visitas]);

    // La selección de Pendientes no aplica a otras pestañas: se limpia al cambiar de tab
    // (paginado/filtros dentro de la misma tab sí la conservan, ver useVisitsSelection).
    const handleTabChange = useCallback(
        (event: React.SyntheticEvent, newTab: number) => {
            clearVisitsSelection();
            handleTabClick(event, newTab);
        },
        [clearVisitsSelection, handleTabClick]
    );

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [selected, setSelected] = useState<VisitaApi | null>(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerAction, setDrawerAction] = useState<DrawerAction | null>(null);
    const [isShowMoreFiltersOpen, setIsShowMoreFiltersOpen] = useState(false);

    const handleAccionClick = (event: React.MouseEvent<HTMLElement>, visita: VisitaApi) => {
        setAnchorEl(event.currentTarget);
        setSelected(visita);
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
    };

    // El segundo parametro es opcional para no romper las llamadas existentes desde el
    // menu de acciones por fila (que ya dejaron `selected` seteado via handleAccionClick).
    // La accion masiva de "Programar visita" del banner SI lo pasa, en null, para no
    // arrastrar una `selected` individual que haya quedado de un click anterior.
    const handleOpenDrawer = (action: DrawerAction, visita: VisitaApi | null = selected) => {
        setDrawerAction(action);
        setSelected(visita);
        setDrawerOpen(true);
    };

    const handleCloseDrawer = () => {
        setDrawerOpen(false);
        setDrawerAction(null);
    };

    const handleSetFilters = useCallback(
        (newFilters: any) => {
            const resolved = typeof newFilters === 'function' ? newFilters(filtros) : newFilters;
            onPaginationChange({ skip: resolved.skip, take: resolved.take });
        },
        [filtros, onPaginationChange]
    );

    const canProgramVisit = selected?.estado === VisitaEstado.PENDIENTE;

    const canRegisterVisit =
        selected?.estado === VisitaEstado.PROGRAMADA ||
        selected?.estado === VisitaEstado.EN_PROCESO;

    const canRejectVisit =
        selected?.estado !== undefined && REJECTABLE_VISIT_STATES.includes(selected.estado);

    const canSeeExecution =
        selected?.estado !== undefined && !ESTADOS_ACTIVOS.includes(selected.estado);

    const hasActividad = (visita: any, description: 'VEROSIMILITUD' | 'RGRL'): boolean =>
        visita?.actividades?.some(
            (a: any) => a?.tipoVisita?.descripcion?.toUpperCase() === description
        ) ?? false;

    const canDownloadOriginalActa =
        selected?.estado !== undefined && ACTA_DOWNLOAD_ALLOWED_STATES.includes(selected.estado);

    const canDownloadVerosimilitud = hasActividad(selected, 'VEROSIMILITUD');
    const canDownloadRgrl = hasActividad(selected, 'RGRL');

    // PDF de investigacion (SMP-401): el Preventor ve sus visitas de investigacion en esta grilla.
    const canDownloadPdfInvestigacion = puedeDescargarPdfInvestigacion(
        selected,
        isPreventor || isAdminBO
    );

    // ZIP con la "Documentación adicional" (SMP-551): los mismos roles que el informe de
    // investigacion, en los estados de En revisión y Cerradas, solo si la visita tiene adjuntos.
    const canDownloadInformacionAdicional = puedeDescargarInformacionAdicional(
        selected,
        Boolean(isPreventor || isAdminBO)
    );

    const handleDownloadActa = async () => {
        try {
            if (!selected) return;
            dispatch(
                showAlert({
                    icon: 'info',
                    message: t('alerts.downloadActa.generating'),
                    loading: true
                })
            );
            const response = await downloadVisitaActa({
                idVisita: (selected as any).idVisita || selected.id,
                idTipoVisita: (selected as any).idTipoVisita,
                tipoActa: 1
            }).unwrap();
            if (response?.data) {
                const byteArray = new Uint8Array(
                    Array.from(atob(response.data), (c) => c.charCodeAt(0))
                );
                const url = window.URL.createObjectURL(
                    new Blob([byteArray], { type: 'application/pdf' })
                );
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `${t('table.actions.downloadActaFilename')}.pdf`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
                dispatch({ type: 'application/hideAlert' });
                enqueueSnackbar(t('alerts.downloadActa.success'), { variant: 'success' });
            }
        } catch (error: any) {
            dispatch({ type: 'application/hideAlert' });
            let errorMessage = t('alerts.downloadActa.error');
            try {
                if (error?.data instanceof Blob) {
                    const json = JSON.parse(await error.data.text());
                    errorMessage = json?.message || errorMessage;
                } else if (error?.data?.message) {
                    errorMessage = error.data.message;
                }
            } catch {}
            dispatch(showAlert({ icon: 'error', message: errorMessage }));
        }
    };

    const handleDownloadVerosimilitud = async () => {
        try {
            if (!selected) return;
            dispatch(
                showAlert({
                    icon: 'info',
                    message: t('alerts.downloadVerosimilitud.generating'),
                    loading: true
                })
            );
            const response = await downloadVisitaActa({
                idVisita: (selected as any).idVisita || selected.id,
                idTipoVisita: (selected as any).idTipoVisita,
                tipoActa: 2
            }).unwrap();
            if (response?.data) {
                const byteArray = new Uint8Array(
                    Array.from(atob(response.data), (c) => c.charCodeAt(0))
                );
                const url = window.URL.createObjectURL(
                    new Blob([byteArray], { type: 'application/pdf' })
                );
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute(
                    'download',
                    `${t('table.actions.downloadVerosimilitudFilename')}.pdf`
                );
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
                dispatch({ type: 'application/hideAlert' });
                enqueueSnackbar(t('alerts.downloadVerosimilitud.success'), { variant: 'success' });
            }
        } catch (error: any) {
            dispatch({ type: 'application/hideAlert' });
            let errorMessage = t('alerts.downloadVerosimilitud.error');
            try {
                if (error?.data instanceof Blob) {
                    const json = JSON.parse(await error.data.text());
                    errorMessage = json?.message || errorMessage;
                } else if (error?.data?.message) {
                    errorMessage = error.data.message;
                }
            } catch {}
            dispatch(showAlert({ icon: 'error', message: errorMessage }));
        }
    };

    const handleDownloadRgrl = async () => {
        try {
            if (!selected || !(selected as any)?.rgrl?.id) {
                dispatch(
                    showAlert({ icon: 'error', message: t('alerts.downloadRgrl.notFoundId') })
                );
                return;
            }
            dispatch(
                showAlert({
                    icon: 'info',
                    message: t('alerts.downloadRgrl.generating'),
                    loading: true
                })
            );
            const response = await getPresentacionRGRLPDFbyId({
                idPresentacion: (selected as any)?.idPresentacion || (selected as any)?.rgrl?.id,
                typeId: 2
            }).unwrap();
            if (response?.data?.presignedUrls?.length > 0) {
                const url = response.data.presignedUrls[0];
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute(
                    'download',
                    `${t('table.actions.downloadRgrlFilename')}_${(selected as any)?.idPresentacion}.pdf`
                );
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
                dispatch({ type: 'application/hideAlert' });
                enqueueSnackbar(t('alerts.downloadRgrl.success'), { variant: 'success' });
            } else {
                throw new Error('No se recibió el archivo.');
            }
        } catch (error: any) {
            dispatch({ type: 'application/hideAlert' });
            dispatch(
                showAlert({
                    icon: 'error',
                    message: error?.message || t('alerts.downloadRgrl.error')
                })
            );
        }
    };

    return (
        <>
            <SectionHeader>
                <SectionHeader.Title>Visitas ROAM</SectionHeader.Title>
            </SectionHeader>

            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <SearchToolbar
                        onChange={() => {}}
                        inputSearchName="visita-roam"
                        label="Buscar visita"
                        showSearchInput={false}
                        onClick={() => {}}
                        clearFilters={onLimpiarFiltros}
                        showMoreFiltersButton={true}
                        isShowMoreFiltersOpen={isShowMoreFiltersOpen}
                        handleShowMoreFiltersPress={() => setIsShowMoreFiltersOpen((prev) => !prev)}
                    >
                        <FiltrosVisitasRoam
                            filtros={filtros}
                            onFiltroChange={onFiltroChange}
                            isShowMoreFiltersOpen={isShowMoreFiltersOpen}
                        />
                    </SearchToolbar>
                </Grid>
            </Grid>

            <Tabs
                value={currentTabIndex}
                onChange={handleTabChange}
                sx={{ height: 64, paddingBottom: '24px', justifyContent: 'center' }}
            >
                {isAdminBO && (
                    <Tab
                        label={t('table.tabs.all')}
                        value={VISIT_TABS_INDEX.ALL}
                        sx={{ fontSize: '1rem', fontWeight: 'bold', px: 4, py: 2 }}
                    />
                )}
                {isAdminBO && (
                    <Tab
                        label={t('table.tabs.rejected')}
                        value={VISIT_TABS_INDEX.REJECTED}
                        sx={{ fontSize: '1rem', fontWeight: 'bold', px: 4, py: 2 }}
                    />
                )}
                <Tab
                    label={t('table.tabs.pending')}
                    value={VISIT_TABS_INDEX.PENDING}
                    sx={{ fontSize: '1rem', fontWeight: 'bold', px: 4, py: 2 }}
                />
                <Tab
                    label={t('table.tabs.programmed')}
                    value={VISIT_TABS_INDEX.PROGRAMMED}
                    sx={{ fontSize: '1rem', fontWeight: 'bold', px: 4, py: 2 }}
                />
                <Tab
                    label={t('table.tabs.inProgress')}
                    value={VISIT_TABS_INDEX.IN_PROGRESS}
                    sx={{ fontSize: '1rem', fontWeight: 'bold', px: 4, py: 2 }}
                />
                <Tab
                    label={t('table.tabs.inRevision')}
                    value={VISIT_TABS_INDEX.IN_REVISION}
                    sx={{ fontSize: '1rem', fontWeight: 'bold', px: 4, py: 2 }}
                />
                <Tab
                    label={t('table.tabs.closed')}
                    value={VISIT_TABS_INDEX.CLOSED}
                    sx={{ fontSize: '1rem', fontWeight: 'bold', px: 4, py: 2 }}
                />
            </Tabs>

            {selectedVisitsIds.length > 0 && canSelectMultipleVisits && (
                <Grid
                    container
                    alignItems="center"
                    justifyContent="space-between"
                    mb={2}
                    p={2}
                    borderRadius={1}
                    gap={2}
                >
                    <Grid item>
                        <Typography fontWeight={700}>
                            {t('drawerProgramVisit.selectedCount', {
                                count: selectedVisitsIds.length
                            })}
                        </Typography>
                    </Grid>
                    <Grid item>
                        <Stack direction="row" spacing={2}>
                            <Button
                                variant="contained"
                                onClick={() =>
                                    handleOpenDrawer(DRAWER_ACTION_VALUES.PROGRAM_VISIT, null)
                                }
                            >
                                {t('drawerProgramVisit.buttonOpen')}
                            </Button>
                            <Button variant="outlined" onClick={clearVisitsSelection}>
                                {t('drawerProgramVisit.buttonClear')}
                            </Button>
                        </Stack>
                    </Grid>
                </Grid>
            )}

            <GrillaVisitasRoam
                visitas={visitas}
                totalCount={totalCount}
                loading={loading}
                filters={filtros}
                setFilters={handleSetFilters}
                onAccionClick={handleAccionClick}
                canSelectVisits={canSelectMultipleVisits}
                isChecked={isChecked}
                handleCheckPress={handleCheckPress}
                isAllChecked={isAllChecked}
                handleAllCheckPress={handleAllCheckPress}
            />

            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleCloseMenu}>
                {canSeeExecution && (
                    <MenuItem
                        onClick={() => {
                            handleCloseMenu();
                            navigate('/backoffice/visitas/ejecucion', {
                                state: {
                                    selectedVisita: selected,
                                    siniestro: (selected as any)?.siniestro,
                                    fromVisitasRoam: true
                                }
                            });
                        }}
                    >
                        Ver ejecución
                    </MenuItem>
                )}
                {canRegisterVisit && (
                    <MenuItem
                        onClick={() => {
                            handleCloseMenu();
                            navigate('/backoffice/visitas/ejecucion', {
                                state: {
                                    selectedVisita: selected,
                                    siniestro: (selected as any)?.siniestro,
                                    fromVisitasRoam: true
                                }
                            });
                        }}
                    >
                        Registrar visita
                    </MenuItem>
                )}
                {canProgramVisit && (
                    <MenuItem
                        onClick={() => {
                            handleCloseMenu();
                            handleOpenDrawer(DRAWER_ACTION_VALUES.PROGRAM_VISIT);
                        }}
                    >
                        Programar visita
                    </MenuItem>
                )}
                {canRejectVisit && (
                    <MenuItem
                        onClick={() => {
                            handleCloseMenu();
                            handleOpenDrawer(DRAWER_ACTION_VALUES.REJECT_VISIT);
                        }}
                    >
                        {t('drawer.rejectVisit')}
                    </MenuItem>
                )}
                {canDownloadOriginalActa && (
                    <>
                        <MenuItem
                            onClick={() => {
                                handleCloseMenu();
                                handleDownloadActa();
                            }}
                        >
                            {t('table.actions.downloadActa')}
                        </MenuItem>
                        {canDownloadVerosimilitud && (
                            <MenuItem
                                onClick={() => {
                                    handleCloseMenu();
                                    handleDownloadVerosimilitud();
                                }}
                            >
                                {t('table.actions.downloadVerosimilitud')}
                            </MenuItem>
                        )}
                        {canDownloadRgrl && (
                            <MenuItem
                                onClick={() => {
                                    handleCloseMenu();
                                    handleDownloadRgrl();
                                }}
                            >
                                {t('table.actions.downloadRgrl')}
                            </MenuItem>
                        )}
                    </>
                )}
                {canDownloadPdfInvestigacion && (
                    // Igual que en la grilla de visitas: el menu queda abierto mientras se genera, con el
                    // item deshabilitado y un spinner (SMP-401).
                    <MenuItem
                        disabled={descargandoPdfInvestigacion}
                        onClick={async () => {
                            await descargarPdfInvestigacion(selected);
                            handleCloseMenu();
                        }}
                    >
                        {t('table.actions.downloadPdfInvestigacion')}
                        {descargandoPdfInvestigacion && (
                            <CircularProgress size={16} sx={{ ml: 1 }} />
                        )}
                    </MenuItem>
                )}
                {canDownloadInformacionAdicional && (
                    // Como en la grilla de visitas: el menu queda abierto con el spinner mientras se arma el
                    // ZIP (SMP-551).
                    <MenuItem
                        disabled={descargandoInformacionAdicional}
                        onClick={async () => {
                            await descargarInformacionAdicional(selected);
                            handleCloseMenu();
                        }}
                    >
                        {t('table.actions.downloadInformacionAdicional')}
                        {descargandoInformacionAdicional && (
                            <CircularProgress size={16} sx={{ ml: 1 }} />
                        )}
                    </MenuItem>
                )}
            </Menu>

            <DrawerManager
                open={drawerOpen}
                setOpen={setDrawerOpen}
                actionType={drawerAction}
                closeDrawer={handleCloseDrawer}
                selected={selected as any}
                selectedVisitsIds={selectedVisitsIds}
                selectedVisitsSummary={selectedVisitsSummary}
                clearVisitsSelection={clearVisitsSelection}
                translate={t}
            />
        </>
    );
}

export default VisitasRoamIndex;
